/**
 * One reader for API error bodies, and one place that turns one into a sentence.
 *
 * The API speaks two shapes. `AppError` produces `{ error: { code, message } }` — five codes
 * today. Everything else is a bare `HTTPException`, which FastAPI serialises as `{ detail: "..." }`
 * — 377 raise sites across 28 routers. Both are live, and the parser is why that stays a change
 * here rather than at ~30 call sites (POS2-1134 CP2).
 *
 * POS2-1152 made the headline always translated. The server's own text is English and always will
 * be, so it can no longer be the sentence a German user reads first — it rides along on
 * `ApiRequestError.description` and becomes the toast's second line. Nothing is lost; it is
 * demoted. That reverses POS2-1133 pinned decision 5, which chose the specific English sentence
 * over a vague German one — a trade that only had to be made while the two could not both be shown.
 */
import { toast } from "sonner";

import type { TFunction } from "@/i18n/catalog";

/**
 * A parsed error body. `code` is null when the server sent no machine code — still the
 * overwhelming majority of responses. `status` is always known, because the caller holds the
 * `Response`, and it is what makes a translated headline possible without a code.
 */
export type ApiError = { code: string | null; message: string; status: number };

/** Codes the server does send, mapped to the sentence the catalogue holds for them. */
const CODE_KEYS: Record<string, string> = {
  not_found: "errors.notFound",
  permission_denied: "errors.permissionDenied",
  validation_error: "errors.validationError",
  invalid_uuid: "errors.invalidUuid",
  conflict: "errors.conflict",
};

/**
 * The sentence for a status when no code came with it.
 *
 * 404, 403 and 422 reuse the coded sentences rather than getting their own: one meaning, one
 * string, so a copy edit cannot leave two versions of it in the catalogue. 401 is the auth
 * redirect's business and 410 is a single raise site; both land on `errors.unknown`.
 */
const STATUS_KEYS: Record<number, string> = {
  400: "errors.badRequest",
  403: "errors.permissionDenied",
  404: "errors.notFound",
  409: "errors.conflict",
  422: "errors.validationError",
  500: "errors.serverError",
  502: "errors.serverError",
  503: "errors.serverError",
  504: "errors.serverError",
};

/** Read an error response in either wire shape. A system boundary, so it validates rather than
 * trusting: the body is whatever the network produced, including an HTML error page. */
export async function parseApiError(response: Response): Promise<ApiError> {
  const status = response.status;
  const body: unknown = await response.json().catch(() => null);
  if (!body || typeof body !== "object") return { code: null, message: "", status };

  const { error, detail } = body as { error?: unknown; detail?: unknown };

  if (error && typeof error === "object") {
    const { code, message } = error as { code?: unknown; message?: unknown };
    if (typeof code === "string" && typeof message === "string") return { code, message, status };
  }

  if (typeof detail === "string") return { code: null, message: detail, status };
  // FastAPI's own 422 puts an array of per-field objects in `detail`. There is nothing in it a
  // user should read, so it becomes the code and the catalogue supplies the sentence.
  if (Array.isArray(detail)) return { code: "validation_error", message: "", status };

  return { code: null, message: "", status };
}

/**
 * The headline to show for a parsed error — always from the catalogue, never the server's English.
 *
 * An unrecognised code falls to the status rather than to the server text, so a code the frontend
 * has not learned yet still reads as the right kind of failure in the reader's language.
 */
export function errorMessage(t: TFunction, error: ApiError): string {
  const key = (error.code ? CODE_KEYS[error.code] : undefined) ?? STATUS_KEYS[error.status];
  return key ? t(key) : t("errors.unknown");
}

/**
 * Carries the server's English text past the `throw` that separates a failed fetch from the
 * handler that renders it.
 *
 * A plain `Error` has one string, so the specificity POS2-1133 decision 5 was protecting had
 * nowhere to go once the headline became translated. This is that somewhere: "Site name already in
 * use" still reaches the reader, under a German headline rather than instead of one.
 */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly description: string,
    /** The HTTP status, kept because some statuses are answers rather than failures: a report
     * preview 409s when the period predates the site going live, and the page says so rather than
     * showing an error (POS2-1137). Parsed all along; it used to be dropped here. */
    readonly status: number = 0,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

/** Read a failed response and build the error to throw. The shape every call site uses. */
export async function apiError(t: TFunction, response: Response): Promise<ApiRequestError> {
  const parsed = await parseApiError(response);
  return new ApiRequestError(errorMessage(t, parsed), parsed.message, parsed.status);
}

/**
 * Show a thrown error under the sentence its code or status earned. One function rather than a
 * toast call per site, so the second line cannot be forgotten at the twenty-ninth of them.
 *
 * Passed by reference as a mutation's `onError`, so it takes exactly one argument. An optional
 * second one would silently collect react-query's `variables` — a `File` as the headline.
 */
export function showApiError(error: Error): void {
  showApiErrorAs(error.message, error);
}

/**
 * Show one under an operation-specific headline the call site supplies.
 *
 * "Couldn't start processing" beats "That request couldn't be completed", and only the call site
 * knows which operation failed — the catalogue cannot infer it from a status. Headline first, so
 * this cannot be handed to `onError` by mistake.
 *
 * The description is dropped when it repeats the headline or is empty, so a toast never says the
 * same thing twice.
 */
export function showApiErrorAs(headline: string, error: Error): void {
  const description =
    error instanceof ApiRequestError && error.description && error.description !== headline
      ? error.description
      : undefined;
  toast.error(headline, description ? { description } : undefined);
}
